/**
 * Financial Statement Item Drawer Component
 * 
 * Complete drawer solution including:
 * - Form component for adding/editing line items
 * - Drawer wrapper with state management
 * - Search functionality for cycles and processes
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import styled from 'styled-components';
import { SearchOutlined, DownOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import DollarSignIcon from '../../../assets/icons/ic-dollar-sign.svg?react';
import { 
  searchCycles, 
  searchProcesses,
  CycleItem,
  ProcessItem 
} from '../../../services/hierarchyService';
import ButtonGroup from '../../../components/common/ButtonGroup';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

// ========== TYPE DEFINITIONS ==========

export interface LineItemFormData {
  id?: any; // Optional id for edit mode
  name: string;
  amount: number;
  isImmaterial: boolean;
  cycles: CycleItem[];
  processes: ProcessItem[];
}

export interface FinancialStatementItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: Partial<LineItemFormData>;
  annualReportId: string;
  onClose: () => void;
  onSubmit: (values: LineItemFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SwitchLabel = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const AmountInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #D1D5DB;
  border-radius: 8px !important;
  overflow: hidden;
  background: white;
  height: 44px;

  &:focus-within {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }

  .ant-input-number {
    flex: 1;
    border: none !important;
    box-shadow: none !important;
    height: 100%;
    background: transparent;
    display: flex;
    align-items: center;

    &:focus,
    &:hover,
    &.ant-input-number-focused {
      border-color: transparent !important;
      box-shadow: none !important;
      outline: none !important;
    }

    .ant-input-number-input-wrap {
      height: 100%;
      display: flex;
      align-items: center;
    }

    .ant-input-number-input {
      height: 100%;
      padding: 0 16px;
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      line-height: 44px;
      letter-spacing: 0.005em;
      color: #6B7280;

      &::placeholder {
        color: #9CA3AF;
      }
    }
  }
`;

const DollarIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  height: 100%;
  border-left: 1px solid #E5E7EB;
  background: #F9FAFB;
  flex-shrink: 0;

  svg {
    display: flex;
  }
`;

const StyledCloseIcon = styled(CloseCircleIcon)<{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

// Search UI Components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #2563EB;
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

// Reusable Tag Component
const Tag = styled.span<{ 
  $backgroundColor: string; 
  $color: string;
  $isMoreTag?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$color};
  cursor: ${props => props.$isMoreTag ? 'pointer' : 'default'};
  transition: all 0.2s;

  ${props => props.$isMoreTag && `
    &:hover {
      opacity: 0.9;
    }
  `}
`;

// ========== MAIN COMPONENT ==========

const FinancialStatementItemDrawer: React.FC<FinancialStatementItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  annualReportId,
  onClose,
  onSubmit,
}) => {
  // Form states
  const [form] = Form.useForm();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState<number | undefined>(undefined);
  const [isImmaterial, setIsImmaterial] = useState(false);
  const [selectedCycles, setSelectedCycles] = useState<CycleItem[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ProcessItem[]>([]);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchType, setSearchType] = useState<'cycle' | 'process'>('cycle');
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedCycles, setTempSelectedCycles] = useState<CycleItem[]>([]);
  const [tempSelectedProcesses, setTempSelectedProcesses] = useState<ProcessItem[]>([]);
  
  // Search results states
  const [cycleResults, setCycleResults] = useState<CycleItem[]>([]);
  const [processResults, setProcessResults] = useState<ProcessItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Cache for cycles (mapping annualReportId -> CycleItem[])
  const [cyclesCache, setCyclesCache] = useState<Record<string, CycleItem[]>>({});

  const { isAdmin } = useSelector((state: RootState) => state.auth);

  // Reset form when initialValues change
  useEffect(() => {
    if (initialValues) {
      // Edit mode - load existing values
      setName(initialValues.name || '');
      setAmount(initialValues.amount);
      setIsImmaterial(initialValues.isImmaterial || false);
      setSelectedCycles(initialValues.cycles || []);
      setSelectedProcesses(initialValues.processes || []);
      form.setFieldsValue({
        name: initialValues.name,
        amount: initialValues.amount,
        isImmaterial: initialValues.isImmaterial,
        cycles: initialValues.cycles,
        processes: initialValues.processes,
      });
    } else {
      // Add mode - reset to empty
      resetForm();
    }
  }, [initialValues, form]);

  // Watch for field changes and validate
  useEffect(() => {
    const checkFormValidation = () => {
      const hasName = !!name && name.trim() !== '';
      const hasAmount = amount != null; // Allow any number including negative and zero
      const hasAuditAssociation = isImmaterial || 
        (Array.isArray(selectedCycles) && selectedCycles.length > 0);
      
      const isValid = hasName && hasAmount && hasAuditAssociation;
      setIsFormValid(isValid);
    };

    checkFormValidation();
  }, [name, amount, isImmaterial, selectedCycles, selectedProcesses]);

  const resetForm = () => {
    setName('');
    setAmount(undefined);
    setIsImmaterial(false);
    setSelectedCycles([]);
    setSelectedProcesses([]);
    form.setFieldsValue({
      name: '',
      amount: undefined,
      isImmaterial: false,
      cycles: [],
      processes: [],
    });
  };

  // Search mode handlers
  const enterSearchMode = (type: 'cycle' | 'process') => {
    setSearchType(type);
    setIsSearchMode(true);
    setSearchQuery('');
    
    // Load existing selections into temp states to show selected state
    setTempSelectedCycles([...selectedCycles]);
    setTempSelectedProcesses([...selectedProcesses]);
    
    // For cycles, check cache first
    if (type === 'cycle' && annualReportId && cyclesCache[annualReportId]) {
      // Load from cache immediately
      setCycleResults(cyclesCache[annualReportId]);
    } else {
      // Clear previous results
      setCycleResults([]);
      setProcessResults([]);
    }
  };

  const exitSearchMode = () => {
    setIsSearchMode(false);
    setSearchQuery('');
    setTempSelectedCycles([]);
    setTempSelectedProcesses([]);
    setCycleResults([]);
    setProcessResults([]);
  };

  // Fetch search results when query changes
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!isSearchMode || !annualReportId) return;

      // For cycles with empty query, check cache first
      if (searchType === 'cycle' && searchQuery === '' && cyclesCache[annualReportId]) {
        setCycleResults(cyclesCache[annualReportId]);
      }
      
      setIsSearching(true);
      
      try {
        if (searchType === 'cycle') {
          const results = await searchCycles({
            annualReportId,
            searchTerm: searchQuery,
          });
          setCycleResults(results);
          
          // Cache cycles when searchQuery is empty
          if (searchQuery === '') {
            setCyclesCache(prev => ({
              ...prev,
              [annualReportId]: results,
            }));
          }
        } else {
          // Extract cycleIds from selectedCycles
          const cycleIds = selectedCycles.map(cycle => cycle.cycleId);
          const results = await searchProcesses({
            annualReportId,
            searchTerm: searchQuery,
            cycleIds,
          });
          setProcessResults(results);
        }
      } catch (error) {
        console.error('Search failed:', error);
        // Show empty results on error
        if (searchType === 'cycle') {
          setCycleResults([]);
        } else {
          setProcessResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search (wait 300ms after user stops typing)
    const timeoutId = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType, isSearchMode, annualReportId, selectedCycles]);

  const handleSearchResultClick = (value: CycleItem | ProcessItem) => {
    if (searchType === 'cycle') {
      const cycleValue = value as CycleItem;
      const exists = tempSelectedCycles.some(c => c.cycleId === cycleValue.cycleId);
      
      if (exists) {
        // Deselecting a cycle - just remove it
        // Don't cascade delete processes here - let user decide
        setTempSelectedCycles(tempSelectedCycles.filter(v => v.cycleId !== cycleValue.cycleId));
      } else {
        // Selecting a cycle - just add it
        setTempSelectedCycles([...tempSelectedCycles, cycleValue]);
      }
    } else {
      const processValue = value as ProcessItem;
      const exists = tempSelectedProcesses.some(p => p.processId === processValue.processId);
      if (exists) {
        setTempSelectedProcesses(tempSelectedProcesses.filter(v => v.processId !== processValue.processId));
      } else {
        setTempSelectedProcesses([...tempSelectedProcesses, processValue]);
      }
    }
  };

  const applySearchSelection = () => {
    if (searchType === 'cycle') {
      // Replace cycles with temp selections
      setSelectedCycles(tempSelectedCycles);
      form.setFieldValue('cycles', tempSelectedCycles);
      
      // CASCADE DELETE: Auto-remove processes that don't belong to selected cycles
      // This only happens when user clicks "Done" to confirm changes
      const selectedCyclePrefixes = new Set(
        tempSelectedCycles.map(c => c.cycleCode.charAt(0))
      );
      const filteredProcesses = tempSelectedProcesses.filter(p => 
        selectedCyclePrefixes.has(p.processCode.charAt(0))
      );
      
      setSelectedProcesses(filteredProcesses);
      form.setFieldValue('processes', filteredProcesses);
    } else {
      // Replace processes with temp selections
      setSelectedProcesses(tempSelectedProcesses);
      form.setFieldValue('processes', tempSelectedProcesses);
    }
    
    exitSearchMode();
  };

  const handleClose = () => {
    setIsFormValid(false);
    setIsSearchMode(false);
    resetForm();
    onClose();
  };

  // Handle drawer close button (X) - context-aware
  const handleDrawerClose = () => {
    if (isSearchMode) {
      // If in search mode, exit search mode and go back to form
      exitSearchMode();
    } else {
      // If in normal mode, close the entire drawer
      handleClose();
    }
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        name,
        amount: amount ?? 0,
        isImmaterial,
        cycles: selectedCycles,
        processes: selectedProcesses,
      });
      handleClose();
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Render selected tags
  // Helper function to remove cycle and its associated processes
  const handleRemoveCycle = (cycleToRemove: CycleItem) => {
    // Remove the cycle
    const updatedCycles = selectedCycles.filter(c => c.cycleId !== cycleToRemove.cycleId);
    setSelectedCycles(updatedCycles);
    form.setFieldValue('cycles', updatedCycles);
    
    // Extract the cycle prefix (e.g., "A00" -> "A", "B00" -> "B")
    const cyclePrefix = cycleToRemove.cycleCode.charAt(0);
    
    // Remove all processes that belong to this cycle (matching prefix)
    const updatedProcesses = selectedProcesses.filter(p => {
      const processPrefix = p.processCode.charAt(0);
      return processPrefix !== cyclePrefix;
    });
    
    // Only update if processes were actually removed
    if (updatedProcesses.length !== selectedProcesses.length) {
      setSelectedProcesses(updatedProcesses);
      form.setFieldValue('processes', updatedProcesses);
    }
  };

  const renderTags = (variant: 'cycle' | 'process') => {
    const allValues = variant === 'cycle' ? selectedCycles : selectedProcesses;
    const backgroundColor = isImmaterial ? '#F2F2F2' : variant === 'cycle' ? '#ECFDF5' : '#EFF6FF';
    const color = isImmaterial ? '#76787B' : variant === 'cycle' ? '#115E59' : '#1E40AF';
    const circleFill = isImmaterial ? '#E0E0E0' : variant === 'cycle' ? '#D1FAE5' : '#DBEAFE';
    
    // Get label from item (full name: code - name), truncated to 60 chars max
    const getLabel = (item: CycleItem | ProcessItem) => {
      let label: string;
      if (variant === 'cycle') {
        const cycle = item as CycleItem;
        label = `${cycle.cycleCode} - ${cycle.cycleName}`;
      } else {
        const process = item as ProcessItem;
        label = `${process.processCode} - ${process.processName}`;
      }
      // Truncate to 60 characters max
      if (label.length > 70) {
        return label.substring(0, 70) + '...';
      }
      return label;
    };

    // Empty state: show placeholder text (arrow is rendered separately)
    if (allValues.length === 0) {
      return (
        <span style={{ 
          fontFamily: 'Inter',
          fontWeight: 500,
          fontSize: '14px',
          color: '#9CA3AF'
        }}>
          Search {variant === 'cycle' ? 'Cycle' : 'Process'}
        </span>
      );
    }

    // Show first 4 tags + "+X more" dropdown
    const visibleTags = allValues.slice(0, 4);
    const remainingCount = allValues.length - 4;

    // Create dropdown menu for "+X more"
    const getDropdownMenuItems = (): MenuProps['items'] => {
      return allValues.slice(4).map((value, index) => ({
        key: index,
        label: (
          <Tag
            $backgroundColor={backgroundColor}
            $color={color}
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <span>{getLabel(value)}</span>
            <StyledCloseIcon
              $circleFill={circleFill}
              $strokeColor={color}
              onClick={(e) => {
                e.stopPropagation();
                if (variant === 'cycle') {
                  handleRemoveCycle(value as CycleItem);
                } else {
                  const updated = selectedProcesses.filter(p => p.processId !== (value as ProcessItem).processId);
                  setSelectedProcesses(updated);
                  form.setFieldValue('processes', updated);
                }
              }}
            />
          </Tag>
        ),
      }));
    };

    // Get colors for "+X more" tag
    const moreTagBackground = variant === 'cycle' ? '#14B8A6' : '#2563EB';
    const moreTagColor = '#FFFFFF';

    return (
      <>
        {visibleTags.map((value, index) => (
          <Tag
            key={index}
            $backgroundColor={backgroundColor}
            $color={color}
          >
            {getLabel(value)}
            <StyledCloseIcon
              $circleFill={circleFill}
              $strokeColor={color}
              onClick={(e) => {
                e.stopPropagation();
                if (variant === 'cycle') {
                  handleRemoveCycle(value as CycleItem);
                } else {
                  const updated = selectedProcesses.filter(p => p.processId !== (value as ProcessItem).processId);
                  setSelectedProcesses(updated);
                  form.setFieldValue('processes', updated);
                }
              }}
            />
          </Tag>
        ))}
        
        {remainingCount > 0 && (
          <Dropdown
            menu={{ items: getDropdownMenuItems() }}
            trigger={['click']}
            placement="bottom"
          >
            <Tag
              $backgroundColor={moreTagBackground}
              $color={moreTagColor}
              $isMoreTag={true}
              onClick={(e) => e.stopPropagation()}
            >
              +{remainingCount} more <DownOutlined style={{ fontSize: '10px', marginLeft: '4px', color: moreTagColor }} />
            </Tag>
          </Dropdown>
        )}
      </>
    );
  };

  // Render search UI
  const renderSearchUI = () => {
    // Get search results based on type
    const isSearchCycles = searchType === 'cycle';
    const currentResults = isSearchCycles ? cycleResults : processResults;

    return (
      <SearchContainer>
        <SearchInputWrapper>
          <Input
            prefix={<SearchOutlined />}
            suffix={
              isSearching && searchQuery !== '' ? (
                <LoadingOutlined style={{ color: '#2563EB' }} />
              ) : null
            }
            placeholder={`Search ${isSearchCycles ? 'Cycle' : 'Process'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
        </SearchInputWrapper>

        <SearchResultsList>
          {currentResults.length === 0 ? (
            searchQuery === '' ? (
              <EmptyState>{isSearching ? 'Loading...' : `There are no ${searchType === 'cycle' ? 'cycles' : 'processes'} to display`}</EmptyState>
            ) : (
              <EmptyState>{isSearching ? 'Searching...' : 'There are no results found'}</EmptyState>
            )
          ): (
            currentResults.map((item) => {
              const itemId = isSearchCycles 
                ? (item as CycleItem).cycleId 
                : (item as ProcessItem).processId;
              const itemLabel = isSearchCycles
                ? `${(item as CycleItem).cycleCode} - ${(item as CycleItem).cycleName}`
                : `${(item as ProcessItem).processCode} - ${(item as ProcessItem).processName}`;
              const isSelected = isSearchCycles 
                ? tempSelectedCycles.some(c => c.cycleId === itemId) 
                : tempSelectedProcesses.some(p => p.processId === itemId);
              return (
                <SearchResultItem
                  key={itemId}
                  $selected={isSelected}
                  onClick={() => handleSearchResultClick(item)}
                >
                  <div className="item-label">{itemLabel}</div>
                </SearchResultItem>
              );
            })
          )}
        </SearchResultsList>
      </SearchContainer>
    );
  };

  // Render form UI
  const renderFormUI = () => (
    <StyledForm
      id="financial-statement-form"
      form={form}
      layout="vertical"
      requiredMark={false}
    >
      {/* Line Item Name */}
      <Form.Item
        name="name"
        rules={[
          { required: true, message: 'Please enter a name' },
          { whitespace: true, message: 'Name cannot be empty' },
        ]}
      >
        <div>
          <FormLabel>Financial Statement Line Item</FormLabel>
          <StyledInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder=""
            style={{ height: 44, borderRadius: 6 }}
            spellCheck={true}
          />
        </div>
      </Form.Item>

      {/* Amount */}
      <Form.Item
        name="amount"
        rules={[
          {
            validator: (_, value) => {
              if (value === null || value === undefined || value === '') {
                return Promise.reject('Please enter an amount');
              }
              const numValue = typeof value === 'string' ? parseFloat(value) : value;
              if (isNaN(numValue)) {
                return Promise.reject('Amount must be a number');
              }
              // Allow any number including negative values
              return Promise.resolve();
            },
          },
        ]}
      >
        <div>
          <FormLabel>Amount</FormLabel>
          <AmountInputWrapper>
            <InputNumber
              value={amount}
              onChange={(value) => setAmount(value ?? undefined)}
              placeholder=""
              style={{ width: '100%' }}
              controls={false}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
            <DollarIconWrapper>
              <DollarSignIcon />
            </DollarIconWrapper>
          </AmountInputWrapper>
        </div>
      </Form.Item>

      {/* Immaterial Toggle */}
      <SwitchWrapper>
        <Switch
          checked={isImmaterial}
          onChange={setIsImmaterial}
        />
        <SwitchLabel>Immaterial</SwitchLabel>
      </SwitchWrapper>

      {/* Cycle */}
      <Form.Item name="cycles">
        <div>
          <FormLabel>Cycle</FormLabel>
          <TagContainer 
            onClick={() => {
              if (!isImmaterial && selectedCycles.length === 0) {
                enterSearchMode('cycle');
              }
            }}
            style={{ 
              opacity: isImmaterial ? 0.5 : 1,
              cursor: isImmaterial ? 'not-allowed' : 'pointer',
              pointerEvents: isImmaterial ? 'none' : 'auto'
            }}
          >
            {renderTags('cycle')}
            <ArrowButton
              onClick={(e) => {
                e.stopPropagation();
                if (!isImmaterial) {
                  enterSearchMode('cycle');
                }
              }}
            >
              <RightOutlined />
            </ArrowButton>
          </TagContainer>
        </div>
      </Form.Item>

      {/* Process */}
      <Form.Item name="processes">
        <div>
          <FormLabel>Process</FormLabel>
          <TagContainer 
            onClick={() => {
              if (!isImmaterial && selectedProcesses.length === 0) {
                enterSearchMode('process');
              }
            }}
            style={{ 
              opacity: isImmaterial ? 0.5 : 1,
              cursor: isImmaterial ? 'not-allowed' : 'pointer',
              pointerEvents: isImmaterial ? 'none' : 'auto'
            }}
          >
            {renderTags('process')}
            <ArrowButton
              onClick={(e) => {
                e.stopPropagation();
                if (!isImmaterial) {
                  enterSearchMode('process');
                }
              }}
            >
              <RightOutlined />
            </ArrowButton>
          </TagContainer>
        </div>
      </Form.Item>
    </StyledForm>
  );

  const drawerTitle = mode === 'add' ? 'Add item' : 'Edit row';

  return (
    <RightContentDrawer
      open={open}
      title={drawerTitle}
      width={572}
      onClose={handleDrawerClose}
      footer={
        isSearchMode ? (
          // Search mode footer: Go back + Done buttons
          <ButtonGroup>
            <Button type="default" onClick={exitSearchMode}>
              Go back
            </Button>
            <Button type="primary" onClick={applySearchSelection}>
              Done
            </Button>
          </ButtonGroup>
        ) : (
          // Normal mode footer: Go back + Submit buttons
          <ButtonGroup>
            <Button type="default" onClick={handleClose} disabled={loading}>
              Go back
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!isFormValid || !isAdmin}
              form="financial-statement-form"
              onClick={handleFormSubmit} 
            >
              {mode === 'add' ? 'Add item' : 'Save changes'}
            </Button>
          </ButtonGroup>
        )
      }
    >
      {isSearchMode ? renderSearchUI() : renderFormUI()}
    </RightContentDrawer>
  );
};

export default FinancialStatementItemDrawer;
